import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { paths } from "../../App";
import { createReservation, CreateReservationError } from "../../dao/reservation";
import { PromoStatus } from "../../reducers/availableTicketTypes";
import { CartReducerState } from "../../reducers/cart";
import { ReactComponent as SadNacho } from "../../assets/illustrations/sad_nacho.svg";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevron_down.svg";
import {
  EventImageCrops,
  GroupedTicketTypes,
  ImgixImage,
  PriceMode,
  TicketTypeCartItem,
} from "../../types";
import { convertPriceMode, newPriceMode } from "../../helpers/priceMode";
import PromoInput from "./PromoInput";
import * as styles from "./styles";
import { ReactComponent as Check } from "../../assets/icons/check.svg";
import { ReactComponent as Exclamation } from "../../assets/icons/exclamation.svg";
import CheckoutLockedReservationModal from "../CheckoutLockedReservationModal";
import {
  TicketBox as TBox,
  TicketBoxSkeleton,
  Button,
  InfoBox,
  TicketGroup,
} from "@hooplab/ui-components";
import {
  availabilityTexts,
  checkExceedsMax,
  checkExceedsMaxEvent,
  checkLessThanMin,
  checkNotDivisibleBy,
  confetti2,
  determineGroupAvailability,
  determineGroupState,
  getTicketError,
  getTopError,
  renderPriceModeText,
  stateTexts,
} from "./helpers";
import TicketRow from "./TicketRow";
import CartButton from "./CartButton";
import ReservationExpiring from "../ReservationExpiring";
import MediaQuery from "react-responsive";
import mediaQueries, { upFrom } from "../../styles/mediaQueries";
import VatCustomerPicker from "../VatCustomerPicker";
import { AvailabilityState } from "@hooplab/ui-components/dist/types";

type TicketBoxProps = {
  images: EventImageCrops | null;
  imgixImage: ImgixImage | null;
  start: Date;
  end: Date;
  isCancelled: boolean;
  eventMax: number;
  eventName: string;
  priority: number[];
  eventId: string;
  eventAvailability: AvailabilityState;
  eventStart: Date;
  eventEnd: Date;
  cart: CartReducerState;
  promoCodes: string[];
  ticketTypes: TicketTypeCartItem[];
  groupedTicketTypes: GroupedTicketTypes[];
  requestState: "fetching" | "fetching_with_result" | "fetched" | "error";
  showPromosInput: boolean;
  updateCart: (ticketType: TicketTypeCartItem, quantity: number) => void;
  onAddPromo: (promo: string) => void;
  priceMode: PriceMode;
  promoStatus: PromoStatus;
  resetPromoStatus: () => void;
};

const PER_PAGE = 6;

const TicketBox: React.FC<TicketBoxProps> = ({ requestState, ...props }) => {
  const navigate = useNavigate();
  const [promoOpen, setPromoOpen] = React.useState(false);
  const [checkoutLock, setCheckoutLock] = React.useState(false);
  const [error, setError] = React.useState<CreateReservationError | null>(null);
  const isPast = props.end < new Date();

  const intl = useIntl();
  const ticketBoxRef = React.useRef<HTMLDivElement>(null);
  const [openStates, setOpenStates] = React.useState<{ [key: number]: boolean }>({});

  const [loading, setLoading] = React.useState(false);
  const [itemsToShow, setItemsToShow] = React.useState(PER_PAGE);
  const [exceedsEventLimit, setExceedsEventLimit] = React.useState(false);
  // client errors need list with ids, to handle which ticket types to associate with errors
  const [exceedLimit, setExceedLimit] = React.useState({
    error: false,
    ids: [0],
  });
  const [lessThanMin, setLessThanMin] = React.useState({
    error: false,
    ids: [0],
  });
  const [divisibleBy, setDivisibleBy] = React.useState({
    error: false,
    ids: [0],
  });

  // Toggle function that takes a groupId
  const toggleOpen = (groupId: number) => {
    setOpenStates((prevStates) => {
      const initialState = prevStates.hasOwnProperty(groupId) ? prevStates[groupId] : false;
      return {
        ...prevStates,
        [groupId]: !initialState,
      };
    });
  };

  React.useEffect(() => {
    // If first group is "special", default it to open
    if (
      props.groupedTicketTypes.length > 0 &&
      props.groupedTicketTypes[0].ticketTypeGroup &&
      props.groupedTicketTypes[0].ticketTypeGroup.ticketTypeGroupId < 0
    ) {
      const groupId = props.groupedTicketTypes[0].ticketTypeGroup.ticketTypeGroupId;
      setOpenStates((prevStates) => {
        return {
          ...prevStates,
          [groupId]: true,
        };
      });
    }
  }, [props.groupedTicketTypes]);

  const ticketTypes: TicketTypeCartItem[] = props.groupedTicketTypes
    .filter((gtt) => !gtt.ticketTypeGroup || gtt.ticketTypeGroup.ticketTypeGroupId > -1)
    .flatMap((group) => group.ticketTypes.map((tt) => tt.ticketType));

  const groupQuantities = props.groupedTicketTypes
    .filter((gtt) => !gtt.ticketTypeGroup || gtt.ticketTypeGroup.ticketTypeGroupId > -1)
    .reduce((acc: { [key: number]: number }, g: GroupedTicketTypes) => {
      if (!g.ticketTypeGroup) {
        return acc;
      }
      if (acc[g.ticketTypeGroup.ticketTypeGroupId]) {
        acc[g.ticketTypeGroup.ticketTypeGroupId] =
          acc[g.ticketTypeGroup.ticketTypeGroupId] +
          g.ticketTypes
            .map((tt) => props.cart[tt.ticketType.ticketTypeId] || 0)
            .reduce((acc, s) => s + acc, 0);
        return acc;
      } else {
        acc[g.ticketTypeGroup.ticketTypeGroupId] = g.ticketTypes
          .map((tt) => props.cart[tt.ticketType.ticketTypeId] || 0)
          .reduce((s, acc) => s + acc, 0);
        return acc;
      }
    }, {} as { [key: number]: number });

  const items = ticketTypes.slice(0, itemsToShow);
  const cartSize = Object.values(props.cart).reduce((acc, x) => acc + x, 0);

  const requiredAddonSum: number = ticketTypes.reduce((acc, tt) => {
    acc += tt.requiredAddonProducts.reduce((acc, ap) => {
      const p = newPriceMode(props.priceMode, ap.price, ap.fee, ap.vatFactor);
      return acc + (p.price + p.fee) * (ap.required || 0) * (props.cart[tt.ticketTypeId] || 0);
    }, 0);
    return acc;
  }, 0);

  const ticketSum: number = ticketTypes.reduce((acc, { price, fee, ticketTypeId, vatFactor }) => {
    const p = newPriceMode(props.priceMode, price, fee, vatFactor);
    return acc + (p.price + p.fee) * (props.cart[ticketTypeId] || 0);
  }, 0);

  const sum: number = ticketSum + requiredAddonSum;

  const onSubmitClicked = async () => {
    setExceedLimit({
      error: false,
      ids: [],
    });
    setLessThanMin({
      error: false,
      ids: [],
    });
    setDivisibleBy({
      error: false,
      ids: [],
    });
    const eventCheck = checkExceedsMaxEvent(
      props.eventMax,
      Object.values(props.cart).reduce((el, acc) => el + acc, 0),
    );
    setExceedsEventLimit(eventCheck);
    let check = checkTicketTypeError();
    if (!check && !eventCheck) {
      setLoading(true);
      const success = await onSubmit();
      if (!success) {
        setLoading(false);
      }
    }
    // We do not need to do anything if the reservation is succeeded, we redirect
    // to /event/:eventId/checkout and this component will unmount
  };

  const topLevelError = error && getTopError(error);

  const ticketError = error && getTicketError(error);

  const checkTicketTypeError = () => {
    // failed variable with initial state false, set to true if any fail condition is met
    // sets error states for use by specific ticket type row
    let failed = false;
    let limitTicketTypes: number[] = [];
    let lessTicketTypes: number[] = [];
    let divideTicketTypes: number[] = [];
    ticketTypes.forEach((tt) => {
      if (checkExceedsMax(tt.countRule.max, props.cart[tt.ticketTypeId] || 0)) {
        limitTicketTypes.push(tt.ticketTypeId);
        setExceedLimit({
          error: true,
          ids: limitTicketTypes,
        });
        failed = true;
      }
      if (checkLessThanMin(tt.countRule.min, props.cart[tt.ticketTypeId] || 0)) {
        lessTicketTypes.push(tt.ticketTypeId);
        setLessThanMin({
          error: true,
          ids: lessTicketTypes,
        });
        failed = true;
      }
      if (checkNotDivisibleBy(tt.countRule.mod, props.cart[tt.ticketTypeId || 0])) {
        divideTicketTypes.push(tt.ticketTypeId);
        setDivisibleBy({
          error: true,
          ids: divideTicketTypes,
        });
        failed = true;
      }
    });
    return failed;
  };

  React.useEffect(() => {
    if (props.promoStatus === PromoStatus.SuccessfulPromo) {
      if (ticketBoxRef.current !== null) {
        ticketBoxRef.current.scrollIntoView({ behavior: "smooth" });
      }
      requestAnimationFrame(confetti2);
    }
  }, [props.promoStatus]);

  const onSubmit = async () => {
    setError(null);
    const tickets = Object.keys(props.cart).reduce((acc, ticketTypeId) => {
      const q: number = props.cart[ticketTypeId];
      return [
        ...acc,
        Array(q).fill({
          ticketTypeId: parseInt(ticketTypeId, 10),
          addonProducts: props.ticketTypes
            .find((tt) => tt.ticketTypeId === parseInt(ticketTypeId))
            ?.requiredAddonProducts.flatMap((ap) =>
              Array(ap.required || 0).fill({ addon_product_id: ap.addonProductId, comment: "" }),
            ),
        }),
      ].flat();
    }, [] as { ticketTypeId: number; addonProducts: { addon_product_id: number; comment: string }[] }[]);

    const secretTickets = props.ticketTypes
      .filter(
        (tt) =>
          tt.isSecret &&
          props.promoCodes.filter((p) => p.toLowerCase() === tt.secretKey?.toLowerCase()).length >
            0,
      )
      .map((tt) => tt.secretKey || "");

    const reservation = await createReservation(props.eventId, tickets, secretTickets);

    if (reservation.status === "fetched") {
      const { hasForms } = reservation.entity;

      const hasAddons = props.ticketTypes
        .filter((tt) =>
          Object.keys(props.cart)
            .filter((key) => props.cart[key] > 0)
            .includes(tt.ticketTypeId.toString()),
        )
        .some(
          (tt) =>
            tt.hasNonRequiredAddons ||
            tt.requiredAddonProducts.some((ap) => ap.customerPrompt && ap.customerPrompt !== ""),
        );
      navigate(
        hasAddons
          ? paths.addons(props.eventId)
          : hasForms
          ? paths.forms(props.eventId)
          : paths.checkout(props.eventId),
        { state: { from: paths.event(props.eventId) } },
      );
    } else if (reservation.status === "error") {
      if (reservation.error.type === "ReservationCheckoutLocked") {
        setCheckoutLock(true);
      }
      setError(reservation.error);
    }

    return reservation.status === "fetched";
  };

  if (requestState === "fetching" || requestState === "fetching_with_result") {
    return <TicketBoxSkeleton />;
  }

  return (
    <div data-nosnippet>
      <TBox
        image={props.imgixImage}
        images={props.images}
        imageAlt={intl.formatMessage(
          { id: "components.LazyImageRenderer.imageFor", defaultMessage: "Image for" },
          { image: props.eventName },
        )}
        eventFewLeft={props.eventAvailability === "FEW_LEFT"}
        fewLeftMessage={
          <FormattedMessage
            id={"components.TicketBox.InfoBoxes.fewTicketsLeft"}
            defaultMessage={"Few tickets left"}
          />
        }
        env={window.ENVIRONMENT.__IMGIX_ENV__}
        isCancelled={props.isCancelled}
        isPast={isPast}
        cancelledText={
          <FormattedMessage
            id={"components.TicketBox.InfoBoxes.eventCancelled"}
            defaultMessage={"This event is canceled."}
          />
        }
        pastText={
          <FormattedMessage
            id={"components.TicketBox.InfoBoxes.eventPast"}
            defaultMessage={"This event has ended."}
          />
        }
        footerButton={
          <React.Fragment>
            {ticketTypes.length > itemsToShow &&
              props.groupedTicketTypes.length === 1 &&
              !props.groupedTicketTypes[0].ticketTypeGroup && (
                <button
                  onClick={() => {
                    setItemsToShow(itemsToShow + PER_PAGE);
                  }}
                  className={
                    "flex flex-col w-full items-center justify-center bg-transparent pb-3 mb-2 hover:no-underline focus:no-underline hover:opacity-70 underline border-b border-gray-300"
                  }
                >
                  <span className="text-gray-700">
                    <FormattedMessage
                      id={"components.TicketBox.moreTickets"}
                      defaultMessage={"More tickets"}
                    />
                  </span>
                  <ChevronDown height={26} width={34} className="opacity-70" />
                </button>
              )}
            {props.ticketTypes.length > 0 && (
              <CartButton
                ticketSize={cartSize}
                addonSize={0}
                loading={loading}
                onClick={onSubmitClicked}
                sum={sum}
              />
            )}
          </React.Fragment>
        }
        active={!Object.values(props.cart).every((p) => p === 0)}
      >
        {props.ticketTypes.length === 0 ? (
          props.showPromosInput ? (
            <InfoBox type={"NO_TICKETS_BUT_PROMO"}>
              <FormattedMessage
                id={"components.TicketBox.InfoBoxes.noTicketsButPromo"}
                defaultMessage={"You need a promo code to access the tickets to this event."}
              />
            </InfoBox>
          ) : (
            <InfoBox type={"NO_TICKETS"}>
              <FormattedMessage
                id={"components.TicketBox.InfoBoxes.noTickets"}
                defaultMessage={"This event has no tickets for sale."}
              />
            </InfoBox>
          )
        ) : (
          <>
            {topLevelError && (
              <div
                className={
                  "flex px-2 py-3 text-red-700/90 text-sm [span]:flex-1 [span]:ml-6 bg-white rounded-lg gap-4 my-4 items-center"
                }
              >
                <SadNacho />
                <span>{topLevelError}</span>
              </div>
            )}
            {exceedsEventLimit && (
              <div
                className={
                  "flex px-2 py-3 text-red-700/90 text-sm [span]:flex-1 [span]:ml-6 bg-white rounded-lg gap-4 my-4 items-center"
                }
              >
                <span>
                  <FormattedMessage
                    id="components.Cart.maxForEventExceeded"
                    defaultMessage={
                      "Only {n, plural, 0 {0 tickets} one {1 ticket} other {# tickets}} can be bought per purchase."
                    }
                    values={{ n: props.eventMax }}
                  />
                </span>
              </div>
            )}
            <React.Fragment>
              {props.groupedTicketTypes.map((groupedTicketType, index) =>
                groupedTicketType.ticketTypeGroup ? (
                  <TicketGroup
                    name={groupedTicketType.ticketTypeGroup.name}
                    key={groupedTicketType.ticketTypeGroup.ticketTypeGroupId}
                    quantity={groupQuantities[groupedTicketType.ticketTypeGroup.ticketTypeGroupId]}
                    description={groupedTicketType.ticketTypeGroup.description || ""}
                    groupSelected={determineGroupState(groupedTicketType)}
                    groupAvailability={determineGroupAvailability(
                      groupedTicketType,
                      groupQuantities[groupedTicketType.ticketTypeGroup.ticketTypeGroupId],
                    )}
                    stateTexts={stateTexts}
                    availabilityTexts={availabilityTexts}
                    isOpen={!!openStates[groupedTicketType.ticketTypeGroup?.ticketTypeGroupId]}
                    onOpen={() =>
                      toggleOpen(groupedTicketType.ticketTypeGroup?.ticketTypeGroupId || -1)
                    }
                    children={
                      <React.Fragment>
                        {groupedTicketType.ticketTypes.map((tt, index) => (
                          <TicketRow
                            key={index}
                            ticketType={tt.ticketType}
                            priceMode={props.priceMode}
                            quantities={props.cart}
                            eventStart={props.eventStart}
                            eventEnd={props.eventEnd}
                            higherOrderAvailability={determineGroupAvailability(
                              groupedTicketType,
                              groupQuantities[groupedTicketType.ticketTypeGroup!.ticketTypeGroupId],
                            )}
                            onChangeQuantity={props.updateCart}
                            error={
                              ticketError && ticketError.id === tt.ticketType.ticketTypeId
                                ? ticketError.message
                                : null
                            }
                            exceedLimit={exceedLimit}
                            lessThanMin={lessThanMin}
                            divisibleBy={divisibleBy}
                          />
                        ))}
                      </React.Fragment>
                    }
                  />
                ) : (
                  <React.Fragment>
                    {items.map((tt, index) => (
                      <TicketRow
                        key={index}
                        ticketType={tt}
                        eventStart={props.eventStart}
                        eventEnd={props.eventEnd}
                        priceMode={props.priceMode}
                        quantities={props.cart}
                        onChangeQuantity={props.updateCart}
                        higherOrderAvailability={props.eventAvailability}
                        error={
                          ticketError && ticketError.id === tt.ticketTypeId
                            ? ticketError.message
                            : null
                        }
                        exceedLimit={exceedLimit}
                        lessThanMin={lessThanMin}
                        divisibleBy={divisibleBy}
                      />
                    ))}
                  </React.Fragment>
                ),
              )}
            </React.Fragment>
          </>
        )}
      </TBox>
      {checkoutLock && (
        <CheckoutLockedReservationModal
          eventId={props.eventId}
          canComplete={false}
          open={checkoutLock}
          close={() => setCheckoutLock(false)}
        />
      )}

      {!props.isCancelled && !isPast && (
        <div className="mt-4 flex flex-col gap-y-2 text-sm text-gray-700 mb-6 lg:mb-0">
          <MediaQuery {...upFrom(mediaQueries.tablet)}>
            <ReservationExpiring additionalCls="ml-2.5" />
          </MediaQuery>
          {props.ticketTypes.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                marginLeft: "10px",
              }}
            >
              {convertPriceMode(props.priceMode).includes("NO_FEE") ? (
                <Exclamation width={18} className="text-yellow-500" />
              ) : (
                <Check width={18} className="text-hooplaGreen" />
              )}
              <span className={"ml-2 text-gray-700"}>{renderPriceModeText(props.priceMode)}</span>
            </div>
          )}
          <VatCustomerPicker additionalCls="ml-2.5" />
          {props.showPromosInput && (
            <div className="ml-2.5 mt-2">
              <Button
                className={styles.promoButton}
                onClick={() => {
                  if (promoOpen) {
                    props.resetPromoStatus();
                  }
                  setPromoOpen((open) => !open);
                }}
              >
                <FormattedMessage
                  tagName={"span"}
                  id={"components.TicketBox.promo"}
                  defaultMessage={"Add promo code"}
                />
              </Button>
              {promoOpen && (
                <PromoInput
                  onSubmit={props.onAddPromo}
                  loading={props.promoStatus === PromoStatus.Verifying}
                  status={props.promoStatus}
                  resetStatus={props.resetPromoStatus}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TicketBox;
